/**
 * Display labels for a cash receipt's log-cash-receipt task on the receipts
 * list: its status, its outcome, and the task-status filter values. Static
 * per-value `gt` calls — the GT linter rejects dynamic registration strings.
 */

import type { useGT } from 'gt-react';
import type { CashReceiptListItem, CashReceiptListTask } from '@erp/rpc';

const OPEN_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'in_progress',
  'needs_input',
]);

/** Whether the receipt's current logging task is still open. */
export function hasOpenLogTask(receipt: CashReceiptListItem): boolean {
  return receipt.logTask != null && OPEN_STATUSES.has(receipt.logTask.status);
}

type Translate = ReturnType<typeof useGT>;

/** Status (or `none`) → label. Unknown values fall through as-is. */
export function formatLogTaskStatus(status: string, gt: Translate): string {
  const context = 'cash receipt logging task status';
  switch (status) {
    case 'pending':
      return gt('To Do', { context });
    case 'in_progress':
      return gt('In Progress', { context });
    case 'needs_input':
      return gt('Needs Input', { context });
    case 'completed':
      return gt('Done', { context });
    case 'failed':
      return gt('Failed', { context });
    case 'cancelled':
      return gt('Cancelled', { context });
    case 'none':
      return gt('No Task', {
        context: 'cash receipt has never had a logging task',
      });
    default:
      return status;
  }
}

/** Outcome → label. Unknown values (a renamed option) fall through as-is. */
export function formatLogOutcome(outcome: string, gt: Translate): string {
  const context = 'outcome of a cash receipt logging task';
  switch (outcome) {
    case 'keyed':
      return gt('Keyed', { context });
    case 'keyed_externally':
      return gt('Keyed Externally', { context });
    case 'on_account':
      return gt('On Account', { context });
    case 'duplicate':
      return gt('Duplicate', { context });
    case 'not_ours':
      return gt('Not AR Cash', { context });
    case 'rejected':
      return gt('Returned / Bounced', { context });
    case 'obsolete':
      return gt('Obsolete', { context });
    default:
      return outcome;
  }
}

/** Lifecycle status → label. Unknown values fall through as-is. */
export function formatLifecycleStatus(status: string, gt: Translate): string {
  const context = 'where a cash receipt is in being filed';
  switch (status) {
    case 'needs_review':
      return gt('Needs Review', { context });
    case 'needs_remittance':
      return gt('Needs Remittance', { context });
    case 'ready_for_entry':
      return gt('Ready for Entry', { context });
    case 'keyed':
      return gt('Successfully Keyed', { context });
    case 'closed':
      return gt('Closed', { context });
    case 'before_tracking':
      return gt('Before Tracking', { context });
    default:
      return status;
  }
}

/**
 * Pill colors per lifecycle status: a person's action amber, waiting work
 * neutral, ready work blue, keyed green, otherwise closed quiet.
 */
export function lifecycleStatusClass(status: string): string {
  switch (status) {
    case 'needs_review':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'ready_for_entry':
      return 'bg-blue-50 text-blue-800 border-blue-200';
    case 'keyed':
      return 'bg-green-50 text-green-800 border-green-200';
    case 'closed':
    case 'before_tracking':
      return 'text-muted-foreground';
    default:
      return '';
  }
}

/** Pill colors per task status, open work warm, closed work quiet. */
export function logTaskStatusClass(status: string): string {
  switch (status) {
    case 'in_progress':
      return 'bg-blue-50 text-blue-800 border-blue-200';
    case 'needs_input':
      return 'bg-amber-50 text-amber-800 border-amber-200';
    case 'completed':
      return 'bg-green-50 text-green-800 border-green-200';
    case 'failed':
      return 'bg-red-50 text-red-800 border-red-200';
    case 'cancelled':
      return 'text-muted-foreground';
    default:
      return '';
  }
}

/**
 * Who has the task, for the list row: the resolved name, else a generic
 * label by assignee kind (a user missing from the directory, an agent the
 * viewer may not list), else "Unassigned".
 */
export function formatLogTaskAssignee(
  task: Pick<CashReceiptListTask, 'assigneeType'>,
  assigneeName: string | null,
  gt: Translate,
): string {
  if (assigneeName) return assigneeName;
  switch (task.assigneeType) {
    case 'agent':
      return gt('Agent', {
        context: 'cash receipts list, task assigned to an unnamed agent',
      });
    case 'user':
      return gt('Someone', {
        context:
          'cash receipts list, task assigned to a user outside the directory',
      });
    default:
      return gt('Unassigned', {
        context: 'cash receipts list, task has no assignee',
      });
  }
}

/**
 * The one date worth showing next to a task: its due date while open, or
 * when it closed. Null when there is neither.
 */
export function logTaskTiming(
  task: Pick<CashReceiptListTask, 'dueDate' | 'completedAt'>,
):
  | { kind: 'due'; dueDate: string }
  | { kind: 'closed'; closedAt: string }
  | null {
  if (task.completedAt) return { kind: 'closed', closedAt: task.completedAt };
  if (task.dueDate) return { kind: 'due', dueDate: task.dueDate };
  return null;
}
